import { createTemplateScanner } from "./promptHighlight"

type HastNode = {
  type: string
  tagName?: string
  value?: string
  children?: HastNode[]
  properties?: Record<string, unknown>
}

/** Code samples keep their braces — a JSON example in a fence is not a template token. */
const OPAQUE_TAGS = new Set(["code", "pre"])

const span = (className: string, value: string): HastNode => ({
  type: "element",
  tagName: "span",
  properties: { className: [className] },
  children: [{ type: "text", value }]
})

/**
 * Colours template tokens in the rendered preview the same way the input side does.
 *
 * The preview is a hast tree, so a gated region is not one string: its tag sits in one node and
 * the rule it gates in the next. Hence one scanner for the whole tree, walked in document order —
 * it carries the open-region state across nodes so a body spanning several paragraphs still
 * reads as gated.
 *
 * Additive: `@uiw/react-markdown-preview` concatenates this into its own plugin list, so raw
 * HTML, rewrite and prism still run.
 */
export const rehypePromptTokens = () => (tree: HastNode) => {
  const scan = createTemplateScanner()

  const walk = (node: HastNode) => {
    if (!node.children?.length) return
    if (node.tagName && OPAQUE_TAGS.has(node.tagName)) return

    const next: HastNode[] = []
    for (const child of node.children) {
      if (child.type === "text" && typeof child.value === "string") {
        for (const piece of scan(child.value)) {
          next.push(
            piece.className
              ? span(piece.className, piece.text)
              : { type: "text", value: piece.text }
          )
        }
      } else {
        walk(child)
        next.push(child)
      }
    }
    node.children = next
  }

  walk(tree)
}
